import { Frame, Head, Label, Node, fx } from './frame';
import { useStrings } from '../../../state/useLocale';
import type { FigureProps } from './index';

/**
 * The four figures about where a change physically is.
 *
 * Git's hardest early idea is that an edit lives in one of three places and
 * that the commands are named after the move, not the place. So `three-trees`
 * draws the three places and walks one change across them, `reset-modes`
 * measures each flag by how far back through those same three places it
 * reaches, `stash` lifts the change out of them entirely, and `revert` is the
 * one that touches none of them — it appends.
 *
 * The three trees keep the same left-to-right order in every figure here, and
 * the change is always the same small green square, so the four drawings can
 * be read as one.
 *
 * The names of the three places are prose and come from the catalogue;
 * `HEAD`, `git add`, `git commit`, `stash@{0}`, the flags and the filenames
 * are what the learner types, and are literals here. See frame.tsx, `Label`.
 */

/* ── three-trees ─────────────────────────────────────────────────────────────
   Working tree, index, HEAD, with the command that moves a change between each
   pair written above the gap it crosses. The change hops rather than slides:
   it waits in each tree until a command is run, which is the point. */

export function ThreeTreesFigure({ active }: FigureProps) {
  const f = useStrings().warmup.fig;
  // The third name is the one the learner types, which is why it is the
  // bright one and why it is the one that is not translated.
  const trees = [
    { id: 'working', x: 10, mid: 52, name: f.workingTree, state: f.edited, token: false },
    { id: 'index', x: 118, mid: 160, name: f.index, state: f.staged, token: false },
    { id: 'head', x: 226, mid: 268, name: 'HEAD', state: f.committed, token: true },
  ];

  return (
    <Frame name="three-trees" active={active}>
      {trees.map((tree, i) => (
        <g key={tree.id} className="fx-rise" style={fx(i * 80)}>
          <rect className="box" x={tree.x} y={46} width={84} height={52} rx={2} />
          {tree.token ? (
            <text className="lbl-ink" x={tree.mid} y={64} textAnchor="middle">
              {tree.name}
            </text>
          ) : (
            <Label x={tree.mid} y={64}>
              {tree.name}
            </Label>
          )}
          <Label className="lbl-dim" x={tree.mid} y={116}>
            {tree.state}
          </Label>
        </g>
      ))}

      <g className="fx-in" style={fx(300)}>
        <text x={106} y={38} textAnchor="middle">
          git add
        </text>
        <path className="wire" d="M97 72 H108" />
        <Head x={114} y={72} dir="right" />
      </g>

      <g className="fx-in" style={fx(380)}>
        <text x={214} y={38} textAnchor="middle">
          git commit
        </text>
        <path className="wire" d="M205 72 H216" />
        <Head x={222} y={72} dir="right" />
      </g>

      {/* The change. Drawn where it ends up — in HEAD — and animated back to
          where it started, so the resting diagram is the finished story. */}
      <rect
        className="sig fx-hop"
        style={fx(200, { '--t': '900ms', '--x0': '-216px', '--x1': '-108px' })}
        x={262}
        y={76}
        width={12}
        height={12}
        rx={2}
      />

      <Label className="lbl-dim fx-in" style={fx(1140)} x={160} y={138}>
        {f.oneChangeThreePlaces}
      </Label>
    </Frame>
  );
}

/* ── reset-modes ─────────────────────────────────────────────────────────────
   The three flags, measured. Each bar grows down through the trees its mode
   actually reaches, so the difference between them is a length rather than
   three paragraphs. HEAD is the green one because it is the row all three
   bars cross: every reset moves HEAD, and only the flag decides what else
   moves with it. */

export function ResetModesFigure({ active }: FigureProps) {
  const f = useStrings().warmup.fig;
  const rows = [
    { id: 'head', name: 'HEAD', y: 52, rule: 48, token: true },
    { id: 'index', name: f.index, y: 80, rule: 76, token: false },
    { id: 'working', name: f.workingTree, y: 108, rule: 104, token: false },
  ];
  const bars = [
    { flag: '--soft', mid: 120, x: 105, height: 22, at: 380 },
    { flag: '--mixed', mid: 190, x: 175, height: 50, at: 580 },
    { flag: '--hard', mid: 260, x: 245, height: 78, at: 780 },
  ];

  return (
    <Frame name="reset-modes" active={active}>
      {/* The names hang off the right-hand end of their own gutter, which is
          the left of the drawing in both languages: `at="right"`. */}
      {rows.map((row, i) => (
        <g key={row.id} className="fx-in" style={fx(i * 70)}>
          <path className="rule" d={`M100 ${row.rule} H306`} />
          {row.token ? (
            <text className="lbl-sig" x={92} y={row.y} textAnchor="end">
              {row.name}
            </text>
          ) : (
            <Label x={92} y={row.y} at="right">
              {row.name}
            </Label>
          )}
        </g>
      ))}

      {bars.map((bar) => (
        <g key={bar.flag}>
          <text className="lbl-dim fx-in" style={fx(bar.at - 120)} x={bar.mid} y={24} textAnchor="middle">
            {bar.flag}
          </text>
          <rect
            className="box-raised fx-grow"
            style={fx(bar.at, { '--t': '220ms' })}
            x={bar.x}
            y={34}
            width={30}
            height={bar.height}
            rx={4}
          />
        </g>
      ))}

      <Label className="lbl-dim fx-in" style={fx(1040)} x={190} y={134}>
        {f.resetReach}
      </Label>
    </Frame>
  );
}

/* ── stash ───────────────────────────────────────────────────────────────────
   Work lifted off the desk and onto a shelf. The two chips travel out of the
   working tree, which is what leaves it clean — the "nothing to commit" only
   appears once they have gone, because that is the order it happens in. The
   way back is drawn from the start, ghosted: nothing was thrown away. */

export function StashFigure({ active }: FigureProps) {
  const f = useStrings().warmup.fig;
  const chips = [
    { name: 'cart.js', y: 44, from: 50, at: 560 },
    { name: 'pricing.js', y: 72, from: 78, at: 660 },
  ];

  return (
    <Frame name="stash" active={active}>
      <g className="fx-in" style={fx(0)}>
        <Label className="lbl-dim" x={14} y={28} at="left">
          {f.workingTree}
        </Label>
        <rect className="box" x={14} y={36} width={120} height={74} rx={2} />
      </g>

      <g className="fx-in" style={fx(120)}>
        <text className="lbl-dim" x={186} y={24}>
          {'stash@{0}'}
        </text>
        <rect className="box" x={186} y={32} width={120} height={76} rx={2} />
      </g>

      <g className="fx-in" style={fx(340)}>
        <text x={160} y={44} textAnchor="middle">
          git stash
        </text>
        <path className="wire sig" d="M140 52 H174" />
        <Head className="sig" x={180} y={52} dir="right" />
      </g>

      {chips.map((chip) => (
        <g
          key={chip.name}
          className="fx-slide"
          style={fx(chip.at, { '--t': '400ms', '--dx': '-174px', '--dy': `${chip.from - chip.y}px` })}
        >
          <rect className="box-raised" x={196} y={chip.y} width={100} height={18} rx={2} />
          <text className="lbl-ink" x={204} y={chip.y + 13}>
            {chip.name}
          </text>
        </g>
      ))}

      {/* Not a label: `git status` prints this, word for word, the moment the
          stash lands, and the learner will read it in the terminal a minute
          later. docs/i18n.md §2.1 — if a command would print it, it stays. */}
      <text className="lbl-dim fx-in" style={fx(1080)} x={74} y={80} textAnchor="middle">
        nothing to commit
      </text>

      <g className="fx-in" style={fx(1120)}>
        <path className="wire ghost" d="M180 94 H146" />
        <Head className="ghost" x={140} y={94} dir="left" />
        <text className="lbl-dim" x={160} y={124} textAnchor="middle">
          git stash pop
        </text>
      </g>
    </Frame>
  );
}

/* ── revert ──────────────────────────────────────────────────────────────────
   The one that does not rewrite anything. The bad commit stays exactly where
   it is — same position, same hash, still reachable — and a new commit arrives
   on the end carrying the opposite change. The arc is the only relationship
   between them, and it is drawn as a hint rather than a parent link because
   that is not what it is. */

export function RevertFigure({ active }: FigureProps) {
  const f = useStrings().warmup.fig;

  return (
    <Frame name="revert" active={active}>
      <g className="fx-in" style={fx(0)}>
        <path className="edge" d="M36 70 H216" />
        <Node x={36} y={70} />
        <Node x={96} y={70} />
        <Node x={156} y={70} />
        <Node x={216} y={70} />
      </g>

      <Label className="lbl-dim fx-in" style={fx(300)} x={96} y={94}>
        {f.bug}
      </Label>

      <path className="edge fx-draw" style={fx(700, { '--t': '200ms', '--len': 70 })} d="M216 70 H276" />
      <Node className="fx-drop" style={fx(520)} x={276} y={70} kind="signal" />
      {/* The command, not a word for it: `git revert` is what gets typed. */}
      <text className="lbl-ink fx-in" style={fx(880)} x={276} y={94} textAnchor="middle">
        revert
      </text>

      <g className="fx-in" style={fx(900, { '--t': '320ms' })}>
        <path className="wire ghost" d="M276 62 C232 30, 142 30, 96 60" />
        <Head className="ghost" x={96} y={60} dir="down" />
        <Label className="lbl-dim" x={186} y={28}>
          {f.undoes}
        </Label>
      </g>

      <Label className="lbl-dim fx-in" style={fx(1080)} x={160} y={126}>
        {f.historyGrows}
      </Label>
    </Frame>
  );
}
